import { FC, useContext } from "react";

import { FormType, getCurrencyFromCountry } from "@simplyk/common";

import { FrontendTicketingContext } from "@/contexts/FrontendTicketingContext";
import { useFormV2Context } from "@/features/FormV2/context/FormV2Context";
import { ThemedAuctionClosedChip } from "@/features/FormV2/sharedComponents/ThemedAuctionClosedChip";
import { ThemedAuctionFormDate } from "@/features/FormV2/sharedComponents/ThemedAuctionFormDate";
import { ThemedAuctionFormLocation } from "@/features/FormV2/sharedComponents/ThemedAuctionFormLocation";
import { ThemedFormArchivedChip } from "@/features/FormV2/sharedComponents/ThemedFormArchivedChip";
import { ThemedFormBanner } from "@/features/FormV2/sharedComponents/ThemedFormBanner";
import { ThemedFormCampaignTargetThermometer } from "@/features/FormV2/sharedComponents/ThemedFormCampaignTargetThermometer";
import { ThemedFormDescription } from "@/features/FormV2/sharedComponents/ThemedFormDescription";
import { ThemedFormTitle } from "@/features/FormV2/sharedComponents/ThemedFormTitle";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { ThemedAuctionFormSummaryLayout } from "@/features/FormV2/Step1/Summary/ThemedAuctionFormSummaryLayout";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCampaignTargetThermometerSkeleton } from "@/forms-design-system/FormCampaignTargetThermometer";
import { trpc } from "@/helpers/trpc";

type AuctionFormV2SummaryProps = {
  forceInlined?: boolean;
};

export const AuctionFormV2Summary: FC<AuctionFormV2SummaryProps> = ({ forceInlined }) => {
  const { formObject, description, localeField, formOrganization, hasDate, hasLocation } = useFormV2Context(
    FormType.Ticketing
  );
  const { ticketingIsClosed, selectedOccurrenceWithRates } = useContext(FrontendTicketingContext);

  const endDate = selectedOccurrenceWithRates?.occurrence?.endUtc;

  const { isPreview, currentStepKey: previewCurrentStepKey, placeholders } = usePreviewContext();
  const isThermometerGhostPreview = useIsGhostPreview("FormV2Thermometer");
  const isDescriptionGhostPreview = useIsGhostPreview("FormV2Description");
  const isDateGhostPreview = useIsGhostPreview("TicketingV2DateSummary");
  const isLocationGhostPreview = useIsGhostPreview("TicketingV2AddressSummary");

  const isCampaign = !!formObject.thermometerTarget;

  const { data: campaignVolumeData, isLoading: isCampaignLoading } = trpc.getStoredFormVolume.useQuery(
    {
      formId: formObject.id,
      formType: FormType.Ticketing,
    },
    {
      enabled: isCampaign && !isPreview,
    }
  );

  const currency = getCurrencyFromCountry(formOrganization.country);

  const title = localeField!.title;
  const banner = formObject.bannerUrl
    ? { imageSrc: formObject.bannerUrl }
    : formObject.bannerVideoUrl
      ? { videoSrc: formObject.bannerVideoUrl }
      : undefined;

  const campaign = isCampaign
    ? {
        isLoading: isCampaignLoading,
        currentCollectedAmount: isPreview ? (formObject?.thermometerTarget ?? 0) * 0.3 : (campaignVolumeData ?? 0),
        targetedAmount: formObject.thermometerTarget ?? 0,
        currency,
      }
    : undefined;

  return (
    <ThemedAuctionFormSummaryLayout
      forceInlined={forceInlined}
      slots={{
        banner: banner && <ThemedFormBanner {...banner} />,
        organizationLogo: (
          <FormV2LogoChip organizationName={formObject.organization?.name} logoUrl={formObject.logoUrl} />
        ),
        status: formObject.isArchived ? <ThemedFormArchivedChip /> : ticketingIsClosed && <ThemedAuctionClosedChip />,
        title: <ThemedFormTitle>{title}</ThemedFormTitle>,
        date: hasDate && (
          <ThemedAuctionFormDate
            date={new Date(endDate!)}
            sx={{
              opacity: isDateGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
            }}
          />
        ),
        location: hasLocation && (
          <ThemedAuctionFormLocation
            address={formObject.address!}
            latitude={formObject.coordinates?.latitude ?? undefined}
            longitude={formObject.coordinates?.longitude ?? undefined}
            sx={{
              opacity: isLocationGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
            }}
          />
        ),
        campaignTarget: campaign ? (
          campaign.isLoading ? (
            <FormCampaignTargetThermometerSkeleton />
          ) : (
            <ThemedFormCampaignTargetThermometer
              currentCollectedAmount={campaign.currentCollectedAmount}
              targetedAmount={campaign.targetedAmount}
              currency={campaign.currency}
              sx={{
                opacity: isThermometerGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
              }}
            />
          )
        ) : undefined,
        description: description && (
          <ThemedFormDescription
            description={description}
            isInitiallyExpanded={isPreview && previewCurrentStepKey === "description" && !placeholders.isDescription}
            sx={{
              opacity: isDescriptionGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
            }}
          />
        ),
      }}
    />
  );
};
